import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, FlaskConical, Home as HomeIcon, Beaker, Waves, Truck, MonitorSmartphone, Lock } from "lucide-react";
import Logo from "../components/Logo";
import { IMG } from "../data/content";
import { TESTER_IMG } from "../data/build";
import { effectiveAudience, isAdmin } from "../access";

const MOBILE_IMG = "https://static.prod-images.emergentagent.com/jobs/a99c2969-7ac0-4b5c-adb1-9cb1c6716fac/images/2ea8dea3d6180e731c9b5f3620368adbe7b4bd903e1fbb2110c9df2b48e9821a.jpeg";
const SOFTWARE_IMG = "https://static.prod-images.emergentagent.com/jobs/a99c2969-7ac0-4b5c-adb1-9cb1c6716fac/images/98637e54b0a438855e0e2fef38c0aa2e879de2dd7fa1d6820d286708184b70cd.jpeg";

const PROJECTS = [
  {
    to: "/shop",
    tag: "Project 1 · Live",
    name: "Aqualux Nexus Pool Shop",
    desc: "The in-store bench water tester — probes + triple strip reader, guided by each client's pool profile, with a branded printable report.",
    img: TESTER_IMG.front,
    icon: FlaskConical,
    cta: "Open the project",
    live: true,
  },
  {
    to: "/mobile",
    tag: "Project 2 · Preview",
    name: "Aqualux Nexus Mobile",
    desc: "The portable field tester — the shop tester's core, packaged for on-site pool technicians, syncing results back to the shop.",
    img: MOBILE_IMG,
    icon: Truck,
    cta: "Explore the mobile unit",
    live: true,
  },
  {
    to: "/home",
    tag: "Project 3 · Concept preview",
    name: "Aqualux Nexus Pool Automation",
    desc: "The installed on-site system — a smart controller with a no-replumb backbone and plug-and-play doser modules that report back over WiFi.",
    img: IMG.scene,
    icon: HomeIcon,
    cta: "Preview the concept",
    live: true,
  },
  {
    to: "/software",
    tag: "Project 4 · Live",
    name: "Aqualux Nexus Pool Software",
    desc: "The brains — one client database feeding Shop, Mobile and Home. Guided strip prompts, water-test history and white-label reports.",
    img: SOFTWARE_IMG,
    icon: MonitorSmartphone,
    cta: "Open the software",
    live: true,
  },
];

export default function Hub() {
  const aud = effectiveAudience();
  const admin = isAdmin();
  const banner = admin
    ? { to: "/admin/views", eyebrow: "Admin · Access & Views", title: "Manage the three access levels — Admin, Investor, Manufacturer.", sub: "Preview each view, share the right password and open every confidential document.", cta: "Open Access Center" }
    : aud === "manufacturer"
    ? { to: "/manufacturer", eyebrow: "Build partner", title: "The Aqualux Nexus manufacturing pack — spec, drawings & RFQ.", sub: "Everything a manufacturer needs to quote the build, in one place.", cta: "View the build pack" }
    : { to: "/proposal", eyebrow: "Investor & partner proposal", title: "The full Aqualux Nexus proposal — one platform, four products.", sub: "The vision, the live software, the numbers and the partnership — in one place.", cta: "View the proposal" };
  return (
    <div className="min-h-screen bg-[#FAFCFD]" data-testid="hub-page">
      <div className="absolute inset-x-0 top-0 h-[420px] -z-10 bg-gradient-to-b from-sky-50/80 to-transparent" />
      <header className="mx-auto max-w-7xl px-5 lg:px-8 h-16 flex items-center justify-between">
        <Logo />
        <Link to="/software" data-testid="hub-admin-login-btn" className="inline-flex items-center gap-2 rounded-full bg-slate-900 hover:bg-sky-700 text-white text-sm font-semibold px-4 py-2 transition-colors">
          <Lock className="h-3.5 w-3.5" /> Admin login
        </Link>
      </header>

      <main className="mx-auto max-w-7xl px-5 lg:px-8 pt-10 md:pt-16 pb-20">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-2xl">
          <span className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-3 py-1.5 text-xs font-mono2 text-sky-700 mb-5">
            <Waves className="h-3.5 w-3.5" /> Modular water platform · Four products
          </span>
          <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
            Choose a <span className="aqua-text">project</span>.
          </h1>
          <p className="mt-4 text-lg text-slate-600">
            Four products, one ecosystem — the shop tester, the mobile field unit, the home automation system and the software that ties them together.
          </p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}>
          <Link to={banner.to} data-testid="hub-proposal-cta" className="group mt-8 flex flex-wrap items-center justify-between gap-4 rounded-3xl border border-sky-200 bg-gradient-to-r from-[#0b1f36] to-[#154173] text-white p-6 md:p-7 overflow-hidden">
            <div>
              <span className="font-mono2 text-[10px] uppercase tracking-widest text-sky-300">{banner.eyebrow}</span>
              <p className="font-display text-xl md:text-2xl font-bold mt-1">{banner.title}</p>
              <p className="text-sky-100/80 text-sm mt-1">{banner.sub}</p>
            </div>
            <span className="inline-flex items-center gap-2 rounded-full bg-white text-[#0b1f36] font-semibold px-5 py-2.5 group-hover:gap-3 transition-all shrink-0">
              {banner.cta} <ArrowRight className="h-4 w-4" />
            </span>
          </Link>
        </motion.div>

        <div className="mt-12 grid sm:grid-cols-2 gap-6">
          {PROJECTS.map((p, i) => {
            const Icon = p.icon;
            const inner = (
              <>
                <div className="relative bg-gradient-to-b from-slate-50 to-white p-4 grid-dots">
                  <img src={p.img} alt={p.name} className="w-full h-48 object-cover rounded-2xl" />
                  <span className="absolute top-6 left-6 font-mono2 text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full glass border border-white/60 text-sky-700">{p.tag}</span>
                </div>
                <div className="p-7">
                  <div className="flex items-center gap-2.5 mb-2">
                    <span className="h-9 w-9 rounded-xl bg-sky-50 border border-sky-100 flex items-center justify-center"><Icon className="h-5 w-5 text-sky-600" /></span>
                    <h2 className="font-display text-xl font-bold text-slate-900">{p.name}</h2>
                  </div>
                  <p className="text-slate-600">{p.desc}</p>
                  <span className={`mt-5 inline-flex items-center gap-2 font-semibold ${p.live ? "text-sky-700" : "text-slate-400"} group-hover:gap-3 transition-all`}>
                    {p.cta} {p.live && <ArrowRight className="h-4 w-4" />}
                  </span>
                </div>
              </>
            );
            return (
              <motion.div key={p.name} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 + i * 0.08 }}>
                {p.to ? (
                  <Link to={p.to} className="group block h-full rounded-3xl border border-slate-200 bg-white overflow-hidden hw-card" data-testid={`project-card-${i}`}>{inner}</Link>
                ) : (
                  <div className="group block h-full rounded-3xl border border-slate-200 bg-white overflow-hidden opacity-80" data-testid={`project-card-${i}`}>{inner}</div>
                )}
              </motion.div>
            );
          })}
        </div>

        <div className="mt-8 flex items-center gap-2 text-sm text-slate-400">
          <Beaker className="h-4 w-4" /> Concept-stage prototypes. Figures indicative and subject to engineering validation.
        </div>
      </main>
    </div>
  );
}
